import { useState } from "react";
import { MapPin, Clock, X, Printer, Sparkles, Package, AlertCircle, MoreHorizontal, MessageSquare, ChefHat } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { cn } from "@/lib/utils";
import { elapsedPct, formatElapsed, useLiveElapsedSeconds } from "@/lib/elapsed";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { OVERLAY_SOFT } from "@/components/ui/overlay";
import type { CookingSession, DeckPriority } from "@/types/cooking-session";
import { ingredientLineName } from "@/types/recipe";
import type { Recipe } from "@/types/recipe";
import { richTextHtml } from "@/lib/rich-text";

// ── Types ────────────────────────────────────────────────────────────────────

interface CookingStep {
  step?: number;
  instruction?: string;
  description?: string;
  timer?: number;
  timerUnit?: "min" | "sec" | "hr";
  timerEnabled?: boolean;
}

type ActiveTab = "overview" | "steps" | "ingredients" | "safety" | "notes";

export interface CookingSessionDetailsPanelProps {
  session: CookingSession;
  recipe: Recipe;
  locationName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPauseResume?: () => Promise<void>;
  onComplete?: () => Promise<void>;
  onCancel: () => void;
  onCookMode?: () => void;
  onLabel?: () => void;
  onAiSummary?: () => void;
  onCheckInventory?: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

type DisplayStatus = "Cooking" | "Paused" | "Late" | "Done";

function resolveDisplayStatus(session: CookingSession): DisplayStatus {
  if (session.status === "COMPLETED") return "Done";
  if (session.isLate) return "Late";
  if (session.status === "PAUSED") return "Paused";
  return "Cooking";
}

const STATUS_COLOR: Record<DisplayStatus, string> = {
  Cooking: "bg-(--brand-blue)",
  Paused: "bg-(--color-warning)",
  Late: "bg-(--color-danger)",
  Done: "bg-(--color-success)",
};

const PRIORITY_COLOR: Record<DeckPriority, string> = {
  HIGH: "bg-(--color-priority-high)",
  NORMAL: "bg-(--color-warning)",
  LOW: "bg-(--color-surface-accent) !text-(--color-text-secondary)",
};

function stepTimerMinutes(step: CookingStep): number {
  if (!step.timer) return 0;
  const u = step.timerUnit ?? "min";
  if (u === "sec") return step.timer / 60;
  if (u === "hr") return step.timer * 60;
  return step.timer;
}


// ── Main component ────────────────────────────────────────────────────────────

export function CookingSessionDetailsPanel({
  session,
  recipe,
  locationName,
  open,
  onOpenChange,
  onPauseResume,
  onComplete,
  onCancel,
  onCookMode,
  onLabel,
  onAiSummary,
  onCheckInventory,
}: CookingSessionDetailsPanelProps) {
  const { t } = useLingui();
  const [activeTab, setActiveTab] = useState<ActiveTab>("overview");

  const tabs: { key: ActiveTab; label: string }[] = [
    { key: "overview",    label: t`Overview` },
    { key: "steps",       label: t`Steps` },
    { key: "ingredients", label: t`Ingredients` },
    { key: "safety",      label: t`Safety` },
    { key: "notes",       label: t`Notes` },
  ];

  function handleOpenChange(next: boolean) {
    if (!next) setActiveTab("overview");
    onOpenChange(next);
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right-edge"
        overlayClassName={OVERLAY_SOFT}
        hideClose
        aria-describedby={undefined}
        className="h-[min(56.25rem,calc(100vh-2.5rem))] w-153 shadow-xl"
      >
        {/* ── Header ── */}
        <div className="flex shrink-0 items-start justify-between px-5 pt-5">
          <div className="flex items-center gap-4">
            <div className="relative size-25 shrink-0 overflow-hidden rounded-[8px]">
              {recipe.imageUrl ? (
                <img
                  src={recipe.imageUrl}
                  alt=""
                  className="absolute inset-0 size-full object-cover pointer-events-none"
                />
              ) : (
                <div className="absolute inset-0 bg-(--color-surface-muted)" />
              )}
            </div>

            <div className="flex flex-col gap-3">
              <SheetTitle className="text-2xl font-semibold leading-6 text-(--color-action)">
                {recipe.name}
              </SheetTitle>
              <div className="flex flex-wrap gap-1">
                {locationName && (
                  <MetaChip icon={<MapPin className="size-4" />} label={locationName} />
                )}
                {session.chefName && (
                  <MetaChip label={session.chefName} withAvatar />
                )}
                {session.stationName && (
                  <MetaChip label={session.stationName} />
                )}
                <MetaChip icon={<Clock className="size-4" />} label={t`${session.targetMinutes} min`} />
              </div>
            </div>
          </div>

          <SheetClose className="mt-0.5 shrink-0 text-(--color-text-secondary) transition-colors hover:text-(--color-action)">
            <X className="size-6" />
            <span className="sr-only">{t`Close`}</span>
          </SheetClose>
        </div>

        {/* ── Tab bar ── */}
        <div className="mx-5 mt-5 flex shrink-0 items-center gap-1 rounded-[6px] bg-(--color-surface-muted) p-1">
          {tabs.map(({ key, label }) => (
            <Button
              key={key}
              type="button"
              variant="ghost"
              onClick={() => setActiveTab(key)}
              className={cn(
                "flex h-auto flex-1 items-center justify-center rounded-[6px] px-4 py-2 text-sm transition-colors",
                activeTab === key
                  ? "bg-(--color-surface-content) font-medium text-(--color-action) shadow-[0px_1px_1px_rgba(16,24,40,0.05)] hover:bg-(--color-surface-content)"
                  : "font-normal text-(--color-text-secondary) hover:text-(--color-action)",
              )}
            >
              {label}
            </Button>
          ))}
        </div>

        {/* ── Body (scrollable) ── */}
        {activeTab === "overview" && (
          <OverviewBody
            session={session}
            recipe={recipe}
            onPauseResume={onPauseResume}
            onComplete={onComplete}
          />
        )}
        {activeTab === "steps" && (
          <StepsBody recipe={recipe} />
        )}
        {activeTab === "ingredients" && (
          <IngredientsBody recipe={recipe} onCheckInventory={onCheckInventory} />
        )}
        {activeTab === "safety" && (
          <SafetyBody recipe={recipe} />
        )}
        {activeTab === "notes" && (
          <NotesBody session={session} />
        )}

        {/* ── Footer ── */}
        <Footer onCancel={onCancel} onCookMode={onCookMode} onLabel={onLabel} onAiSummary={onAiSummary} />
      </SheetContent>
    </Sheet>
  );
}

// ── Meta chip ────────────────────────────────────────────────────────────────

function MetaChip({ icon, label, withAvatar }: { icon?: React.ReactNode; label: string; withAvatar?: boolean }) {
  return (
    <div className="flex items-center gap-1 rounded-full bg-(--color-surface-muted) py-2 pl-2 pr-3">
      {withAvatar ? (
        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-(--color-action)/10 text-[10px] font-semibold text-(--color-action)">
          {label.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
        </div>
      ) : (
        icon && <span className="text-(--color-text-secondary)">{icon}</span>
      )}
      <span className="text-sm font-medium leading-none text-(--color-text-secondary) whitespace-nowrap">{label}</span>
    </div>
  );
}

// ── Overview body ─────────────────────────────────────────────────────────────

function OverviewBody({
  session,
  recipe,
  onPauseResume,
  onComplete,
}: {
  session: CookingSession;
  recipe: Recipe;
  onPauseResume?: () => Promise<void>;
  onComplete?: () => Promise<void>;
}) {
  const { t } = useLingui();
  const displayStatus = resolveDisplayStatus(session);

  const STATUS_LABELS: Record<DisplayStatus, string> = {
    Cooking: t`Cooking`,
    Paused: t`Paused`,
    Late: t`Late`,
    Done: t`Done`,
  };

  const servingsText = !recipe.servings
    ? "—"
    : recipe.servingSize
    ? `${recipe.servings} ${recipe.servingSize}`
    : plural(recipe.servings, { one: "# portion", other: "# portions" });

  const steps = (recipe.instructions as CookingStep[] | undefined) ?? [];
  const currentStepIdx = 0;
  const currentStep = steps[currentStepIdx];
  const stepTarget = currentStep ? stepTimerMinutes(currentStep) : session.targetMinutes;
  // Live, and from seconds — the panel used to sit on whatever the last poll
  // said, in whole minutes (T-1101).
  const elapsedSeconds = useLiveElapsedSeconds(session);
  const progressPct = elapsedPct(elapsedSeconds, stepTarget);

  const startedLabel = session.startedAt
    ? new Date(session.startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "—";
  const finishedLabel = session.completedAt
    ? new Date(session.completedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 pb-22">
      {/* Description */}
      <div className="flex flex-col gap-2">
        <p className="text-base font-semibold text-(--color-action)">{t`Overview`}</p>
        {recipe.description ? (
          <div
            className="text-base font-normal text-(--color-action) [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: richTextHtml(recipe.description) }}
          />
        ) : (
          <p className="text-base font-normal text-(--color-action)">{t`No description available.`}</p>
        )}
      </div>

      {/* Info grid */}
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <InfoCard label={t`Status`} className="flex-1">
            <div className="flex flex-wrap gap-2">
              <StatusPill label={STATUS_LABELS[displayStatus]} className={STATUS_COLOR[displayStatus]} />
              {session.priority !== "NORMAL" && (
                <StatusPill
                  label={session.priority === "HIGH" ? t`High` : t`Low`}
                  className={PRIORITY_COLOR[session.priority]}
                />
              )}
              {session.priority === "NORMAL" && (
                <StatusPill label={t`Normal`} className={PRIORITY_COLOR["NORMAL"]} />
              )}
            </div>
          </InfoCard>
          <InfoCard label={t`Servings`} className="flex-1 self-stretch">
            <p className="text-base font-medium text-(--color-action)">{servingsText}</p>
          </InfoCard>
        </div>

        <div className="flex gap-2">
          <InfoCard label={t`Cooking time`} className="flex-1 self-stretch">
            <p className="text-base font-medium text-(--color-action)">
              {recipe.cookTimeMinutes ? t`${recipe.cookTimeMinutes} min` : "—"}
            </p>
          </InfoCard>
          <InfoCard label={t`Prep time`} className="flex-1 self-stretch">
            <p className="text-base font-medium text-(--color-action)">
              {recipe.prepTimeMinutes ? t`${recipe.prepTimeMinutes} min` : "—"}
            </p>
          </InfoCard>
        </div>

        <div className="flex gap-2">
          <InfoCard label={t`Started`} className="flex-1 self-stretch">
            <p className="text-base font-medium text-(--color-action)">{startedLabel}</p>
          </InfoCard>
          <InfoCard label={t`Finished`} className="flex-1 self-stretch">
            <p className={cn("text-base font-medium", session.completedAt ? "text-(--color-action)" : "text-(--color-text-secondary)")}>
              {finishedLabel}
            </p>
          </InfoCard>
        </div>

        {session.status !== "COMPLETED" && (
          <div className="flex flex-col gap-3.5 rounded-[8px] bg-(--color-surface-muted) p-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-base font-medium text-(--color-action)">
                  {currentStep?.instruction ?? recipe.name}
                </p>
                {steps.length > 0 && (
                  <div className="flex items-center justify-center rounded-full bg-(--color-surface-accent) px-3 py-2">
                    <span className="text-xs font-medium leading-none text-(--color-text-secondary) whitespace-nowrap">
                      {t`Step ${currentStepIdx + 1}`}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-normal text-(--color-text-secondary) whitespace-nowrap">
                  {t`Current: ${formatElapsed(elapsedSeconds)}`}
                </span>
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-(--color-surface-content)">
                  <div
                    className="h-full rounded-full bg-(--color-action) transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <span className="text-sm font-normal text-(--color-text-secondary) whitespace-nowrap">
                  {t`Target: ${stepTarget}m`}
                </span>
              </div>
            </div>

            {(onPauseResume ?? onComplete) && (
              <div className="flex items-center justify-end gap-2">
                {onPauseResume && (
                  <Button
                    variant="outline"
                    onClick={onPauseResume}
                    className="h-8 w-42 rounded-[4px] border-(--color-border) bg-(--color-surface-content) text-sm font-medium text-(--color-action) hover:bg-(--color-surface-muted)"
                  >
                    {session.status === "PAUSED" ? t`Resume` : t`Pause`}
                  </Button>
                )}
                {onComplete && (
                  <Button
                    onClick={onComplete}
                    className="h-8 w-42 rounded-[4px] bg-(--color-success) text-sm font-medium text-white hover:bg-(--color-success-hover)"
                  >
                    {t`Complete`}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Steps body ────────────────────────────────────────────────────────────────

function StepsBody({ recipe }: { recipe: Recipe }) {
  const { t } = useLingui();
  const steps = (recipe.instructions as CookingStep[] | undefined) ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 pb-22">
      {/* Section title + description */}
      <div className="flex flex-col gap-2">
        <p className="text-base font-semibold text-(--color-action)">{t`Steps`}</p>
        {recipe.description && (
          <div
            className="text-base font-normal text-(--color-action) [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: richTextHtml(recipe.description) }}
          />
        )}
      </div>

      {/* Step list */}
      {steps.length > 0 ? (
        <div className="flex flex-col gap-2">
          {steps.map((step, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-[8px] bg-(--color-surface-muted) p-3"
            >
              {/* Number badge */}
              <div className="flex size-10 shrink-0 items-center justify-center rounded-[6px] bg-(--color-surface-content) p-2">
                <span className="text-sm font-normal leading-none text-(--color-text-secondary)">{i + 1}</span>
              </div>
              {/* Text */}
              <div className="flex flex-col gap-0.5">
                <p className="text-base font-medium leading-snug text-(--color-action)">
                  {step.instruction ?? t`Step ${i + 1}`}
                </p>
                {step.description && (
                  <div
                    className="text-sm font-normal leading-snug text-(--color-text-secondary) [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
                    dangerouslySetInnerHTML={{ __html: richTextHtml(step.description) }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-(--color-text-secondary)">{t`No steps listed.`}</p>
      )}

      {/* AI Tip card */}
      {recipe.tips && (
        <div className="flex items-center gap-4 rounded-[8px] border border-(--color-border) bg-(--color-surface-muted) p-3">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-[9.6px] bg-(--color-action) p-2.5">
            <Sparkles className="size-6 text-white" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-base font-medium text-(--color-action)">{t`AI Tip`}</p>
            <p className="text-base font-normal text-(--color-text-secondary)">{recipe.tips}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ingredients body ──────────────────────────────────────────────────────────

function IngredientsBody({ recipe, onCheckInventory }: { recipe: Recipe; onCheckInventory?: () => void }) {
  const { t } = useLingui();
  const ingredients = recipe.ingredients ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 pb-22">
      {/* Section title + description */}
      <div className="flex flex-col gap-2">
        <p className="text-base font-semibold text-(--color-action)">{t`Ingredients`}</p>
        {recipe.description && (
          <div
            className="text-base font-normal text-(--color-action) [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: richTextHtml(recipe.description) }}
          />
        )}
      </div>

      {/* Ingredient rows */}
      {ingredients.length > 0 ? (
        <div className="flex flex-col gap-2">
          {ingredients.map((ing) => (
            <div key={ing.id} className="flex items-center justify-between rounded-[8px] bg-(--color-surface-muted) px-3 py-3">
              <p className="text-base font-medium text-(--color-action)">{ingredientLineName(ing)}</p>
              <p className="text-sm font-normal text-(--color-text-secondary) whitespace-nowrap">
                {ing.quantity} {ing.unit}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-(--color-text-secondary)">{t`No ingredients listed.`}</p>
      )}

      {/* Stock check card */}
      <div className="flex items-center justify-between rounded-[8px] border border-(--color-border) bg-(--color-surface-muted) p-3">
        <div className="flex items-center gap-4">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-[9.6px] bg-(--color-action) p-2.5">
            <Package className="size-6 text-white" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-base font-medium text-(--color-action)">{t`Stock check`}</p>
            <p className="text-base font-normal text-(--color-text-secondary)">{t`Flag low inventory before standing.`}</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={onCheckInventory}
          className="h-11 w-45 shrink-0 rounded-[10px] border-(--color-border) bg-(--color-surface-content) text-base font-medium text-(--color-action) hover:bg-(--color-surface-muted)"
        >
          {t`Check inventory`}
        </Button>
      </div>
    </div>
  );
}

// ── Safety body ───────────────────────────────────────────────────────────────

function SafetyBody({ recipe }: { recipe: Recipe }) {
  const { t } = useLingui();
  const allergens = recipe.allergens ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 pb-22">
      {/* Section title + description */}
      <div className="flex flex-col gap-2">
        <p className="text-base font-semibold text-(--color-action)">{t`Safety`}</p>
        {recipe.description && (
          <div
            className="text-base font-normal text-(--color-action) [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: richTextHtml(recipe.description) }}
          />
        )}
      </div>

      {/* Safety cards */}
      <div className="flex flex-col gap-3">
        {/* Allergens card */}
        <SafetyCard
          title={t`Allergens`}
          count={allergens.length}
          description={t`Known allergens present in this recipe.`}
        >
          {allergens.length > 0 ? (
            allergens.map((allergen) => (
              <div key={allergen.id} className="flex items-center gap-3 rounded-[8px] bg-(--color-surface-muted) px-3 py-3">
                <AlertCircle className="size-5 shrink-0 text-(--color-text-secondary)" />
                <p className="text-base font-medium text-(--color-action)">{allergen.name}</p>
              </div>
            ))
          ) : (
            <p className="text-sm font-medium text-(--color-text-secondary)">{t`No data.`}</p>
          )}
        </SafetyCard>

        {/* Labeling requirements card */}
        <SafetyCard
          title={t`Labeling requirements`}
          count={recipe.labelingRequirements ? 1 : 0}
          description={t`Required labels and compliance notes for this recipe.`}
        >
          {recipe.labelingRequirements ? (
            <div className="flex items-center gap-3 rounded-[8px] bg-(--color-surface-muted) px-3 py-3">
              <AlertCircle className="size-5 shrink-0 text-(--color-text-secondary)" />
              <p className="text-base font-medium text-(--color-action)">{recipe.labelingRequirements}</p>
            </div>
          ) : (
            <p className="text-sm font-medium text-(--color-text-secondary)">{t`No data.`}</p>
          )}
        </SafetyCard>
      </div>
    </div>
  );
}

function SafetyCard({
  title,
  count,
  description,
  children,
}: {
  title: string;
  count: number;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-(--color-border) bg-(--color-surface-content) p-3 shadow-[0px_1px_1px_rgba(16,24,40,0.05)]">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <p className="text-base font-medium text-(--color-action)">{title}</p>
          <div className="flex h-6.5 w-10 items-center justify-center rounded-full bg-(--color-surface-muted) px-3 py-1">
            <span className="text-sm font-medium leading-none text-(--color-text-secondary)">{count}</span>
          </div>
        </div>
        <p className="text-sm font-normal text-(--color-text-secondary)">{description}</p>
      </div>

      {/* Divider */}
      <div className="border-t border-(--color-border)" />

      {/* Content */}
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

// ── Notes body ────────────────────────────────────────────────────────────────

function NotesBody({ session }: { session: CookingSession }) {
  const { t } = useLingui();

  // Build a note entry from the session's notes field if present
  const notes = session.notes
    ? [{
        id: session.id,
        author: session.chefName ?? t`Unknown`,
        date: session.startedAt ?? session.createdAt,
        body: session.notes,
      }]
    : [];

  function formatNoteDate(iso: string) {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  }

  function initials(name: string) {
    return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 pb-22">
      {/* Section title + description */}
      <div className="flex flex-col gap-2">
        <p className="text-base font-semibold text-(--color-action)">{t`Notes`}</p>
      </div>

      {/* Note cards */}
      {notes.length > 0 ? (
        <div className="flex flex-col gap-3">
          {notes.map((note) => (
            <div key={note.id} className="flex flex-col gap-3 rounded-xl bg-(--color-surface-muted) p-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex size-6 shrink-0 items-center justify-center rounded-full border border-(--color-border) bg-(--color-action)/10 text-[10px] font-semibold text-(--color-action)">
                    {initials(note.author)}
                  </div>
                  <p className="text-sm font-medium leading-6 text-(--color-action) whitespace-nowrap">
                    {note.author}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-normal leading-6 text-(--color-text-secondary) whitespace-nowrap">
                    {formatNoteDate(note.date)}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-5 text-(--color-text-secondary) hover:text-(--color-action)"
                    aria-label={t`Note options`}
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </div>
              </div>
              {/* Body */}
              <p className="text-sm font-normal leading-6 text-(--color-action)">{note.body}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 py-8 text-(--color-text-secondary)">
          <MessageSquare className="size-8" />
          <p className="text-sm">{t`No notes yet.`}</p>
        </div>
      )}
    </div>
  );
}

// ── Info card ─────────────────────────────────────────────────────────────────

function InfoCard({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col justify-between gap-2 rounded-[8px] bg-(--color-surface-muted) p-3", className)}>
      <p className="text-sm font-normal text-(--color-text-secondary) whitespace-nowrap">{label}</p>
      {children}
    </div>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────

function StatusPill({ label, className }: { label: string; className: string }) {
  return (
    <div className={cn("flex items-center justify-center rounded-full px-2 py-1", className)}>
      <span className="text-sm font-medium leading-none text-white whitespace-nowrap">{label}</span>
    </div>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────

function Footer({
  onCancel,
  onCookMode,
  onLabel,
  onAiSummary,
}: {
  onCancel: () => void;
  onCookMode?: () => void;
  onLabel?: () => void;
  onAiSummary?: () => void;
}) {
  const { t } = useLingui();
  return (
    <div className="absolute bottom-0 left-0 right-0 flex shrink-0 items-center justify-between border-t border-(--color-border) bg-(--color-surface-content) px-5 py-5 shadow-[0px_-8px_16px_rgba(16,24,40,0.04)]">
      <Button
        type="button"
        variant="ghost"
        onClick={onCancel}
        className="h-auto px-0 text-base font-medium text-(--color-action) hover:bg-transparent hover:text-(--color-text-secondary)"
      >
        {t`Cancel`}
      </Button>
      <div className="flex items-center gap-2">
        {onCookMode && (
          <Button
            variant="outline"
            onClick={onCookMode}
            className="h-11 w-40 gap-2 rounded-[10px] border-(--color-border) text-sm font-medium text-(--color-action)"
          >
            <ChefHat className="size-4" />
            {t`Cook Mode`}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={onLabel}
          className="h-11 w-40 gap-2 rounded-[10px] border-(--color-border) text-sm font-medium text-(--color-action)"
        >
          <Printer className="size-5" />
          {t`Label`}
        </Button>
        <Button
          onClick={onAiSummary}
          className="h-11 w-40 gap-2 rounded-[10px] bg-(--color-action) text-sm font-medium text-white hover:bg-(--color-action)/90"
        >
          <Sparkles className="size-4" />
          {t`AI Summary`}
        </Button>
      </div>
    </div>
  );
}
